import { Story } from '../../types.ts';
import {
  fetchWithTimeout,
  parseErrorResponse,
  OfflineQueue,
  storyToServer,
  storyFromServer,
} from './base.ts';

export const storiesApi = {
  getStories: async (projectId: string, filters?: { personId?: string }): Promise<Story[]> => {
    try {
      const params = new URLSearchParams();
      if (filters?.personId) params.set('personId', filters.personId);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const res = await fetchWithTimeout(`/api/projects/${projectId}/stories${qs}`);
      if (!res.ok) await parseErrorResponse(res);
      const rows = await res.json();
      return rows.map(storyFromServer);
    } catch (error) {
      console.warn('⚠️ Database unavailable, returning empty stories list:', error);
      return [];
    }
  },

  saveStory: async (story: Partial<Story>): Promise<Story> => {
    const isUpdate = !!story.id;
    const serverPayload = storyToServer(story);

    try {
      if (isUpdate) {
        const res = await fetchWithTimeout(`/api/stories/${story.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(serverPayload),
        });
        if (!res.ok) await parseErrorResponse(res);
        return storyFromServer(await res.json());
      } else {
        const res = await fetchWithTimeout(`/api/projects/${story.projectId}/stories`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(serverPayload),
        });
        if (!res.ok) await parseErrorResponse(res);
        return storyFromServer(await res.json());
      }
    } catch (error) {
      console.warn('⚠️ Database unavailable, queuing operation:', error);
      const tempId = story.id || crypto.randomUUID();
      OfflineQueue.enqueue({
        operation: isUpdate ? 'UPDATE' : 'CREATE',
        endpoint: isUpdate
          ? `/api/stories/${story.id}`
          : `/api/projects/${story.projectId}/stories`,
        method: isUpdate ? 'PUT' : 'POST',
        data: story,
      });
      return {
        ...story,
        id: tempId,
        created_at: story.created_at || new Date().toISOString(),
        taggedPersonIds: story.taggedPersonIds || [],
        _offline: true,
      } as any;
    }
  },

  deleteStory: async (id: string): Promise<void> => {
    try {
      const res = await fetchWithTimeout(`/api/stories/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) await parseErrorResponse(res);
    } catch (error) {
      console.warn('⚠️ Database unavailable, queuing operation:', error);
      OfflineQueue.enqueue({
        operation: 'DELETE',
        endpoint: `/api/stories/${id}`,
        method: 'DELETE',
        data: {},
      });
    }
  },

  // --- COLLABORA ---
  getCollaboraConfig: async (): Promise<{ enabled: boolean }> => {
    try {
      const res = await fetchWithTimeout('/api/config/collabora');
      if (!res.ok) return { enabled: false };
      return res.json();
    } catch {
      return { enabled: false };
    }
  },

  createCollaboraSession: async (
    storyId: string
  ): Promise<{ formAction: string; accessToken: string; accessTokenTtl: number }> => {
    const res = await fetchWithTimeout(`/api/stories/${storyId}/collabora-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },
};
